export default class AiVision {
  constructor() {
    this.isActive = false;
    this.puterLoaded = false;
    this.currentAudio = null;
    this.latestRequestId = 0;
    this.clickHandler = this.handleClick.bind(this);
  }

  async enable() {
    this.isActive = true;
    document.addEventListener('click', this.clickHandler, true); // Capture phase to preempt others if needed
    document.body.classList.add('a11y-ai-vision-active');
  }

  disable() {
    this.isActive = false;
    document.removeEventListener('click', this.clickHandler, true);
    document.body.classList.remove('a11y-ai-vision-active');
    this.stopPlayback();
  }

  stopPlayback() {
    this.latestRequestId++;
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
  }

  async loadPuter() {
    if (window.puter) {
      this.puterLoaded = true;
      return;
    }
    
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://js.puter.com/v2/';
        script.onload = () => {
            this.puterLoaded = true;
            resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
    });
  }

  async handleClick(event) {
    if (!this.isActive) return;
    const target = event.target;

    // Only interested in Images
    if (target.tagName !== 'IMG') return;

    // Ignore clicks on sidebar, tutorial modal, or system elements
    if (event.target.closest('.a11y-sidebar') || 
        event.target.closest('.a11y-tutorial-modal') ||
        event.target.closest('.a11y-tts-toolbar') ||
        event.target.closest('.a11y-image-caption')) return;

    // Prevent default actions (like links wrapping images)
    event.preventDefault();
    event.stopPropagation();

    this.describeImage(target);
  }

  async describeImage(target) {
    // 1. Check Cache
    if (target.dataset.aiVisionDescription) {
        this.showCaption(target.dataset.aiVisionDescription);
        return;
    }

    // 2. Analyze
    const originalCursor = document.body.style.cursor;
    document.body.style.cursor = 'wait';
    
    // Feedback
    this.showCaption("Analizuję obraz...");

    try {
        if (!this.puterLoaded) await this.loadPuter();
        
        // Fetch Blob
        const response = await fetch(target.src);
        const blob = await response.blob();

        // Convert to Base64
        const base64Image = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });

        const prompt = `Wygeneruj opis ALT zgodny z WCAG dla zdjęcia produktowego w języku polskim.
ALT ma mieć 10–30 słów, być neutralny, konkretny i informacyjny.
Uwzględnij wyłącznie informacje widoczne na zdjęciu: typ produktu + marka/model (jeśli znane) + kolor + widoczna cecha (np. zapięcie, kształt, materiał).
Nie zgaduj wieku, płci, stanu zdrowia ani emocji.
Opisz czynność tylko wtedy, gdy ma znaczenie dla przekazu zdjęcia.
Nie dodawaj zastosowań medycznych, jeśli nie wynikają bezpośrednio ze zdjęcia.

Nie używaj zwrotów: „zdjęcie przedstawia”, „na zdjęciu widać”, „obraz przedstawia”, „grafika pokazuje”, „fotografia produktu”.
Nie używaj marketingowych przymiotników/opinii: „piękny”, „elegancki”, „stylowy”, „modny”, „super”, „najlepszy”, „rewelacyjny”, „wysokiej jakości”, „premium”, „luksusowy”, „wyjątkowy”.
Nie używaj emocji ani domysłów: „zadowolona kobieta”, „uśmiechnięty pacjent”, „szczęśliwe dziecko”, „w relaksie”, „w trakcie leczenia”.
Unikaj przesadnych określeń katalogowych: „bardzo”, „niezwykle”, „maksymalnie”, „w pełni”, „kompletnie”, „rewolucyjny”, „innowacyjny” (chyba że to napis widoczny na produkcie).
Unikaj diagnoz i określeń medycznych, takich jak: „po mastektomii”, „dla osób z nietrzymaniem moczu”, „dla diabetyków”, „rehabilitacyjny”, „ortopedyczny”, „leczniczy”, „terapeutyczny”, „dla pacjentów” — chyba że jest to nazwa kategorii, jest widoczne na produkcie lub wymagane przez kontekst strony.
Jeśli istotne, dodaj rodzaj ujęcia: „widok z przodu”, „widok z boku”, „zbliżenie na detal”.
Nie dopisuj rozmiaru, refundacji, parametrów technicznych ani zastosowań, jeśli nie są widoczne na zdjęciu lub w treści wejściowej.
Jeśli zdjęcie jest wyłącznie dekoracyjne (tło, ozdobnik, pattern), zwróć pusty ALT: "".

Zwróć wyłącznie sam ALT (bez komentarzy, bez cudzysłowów).`;

        // Construct structured message for Vision efficiency and compatibility
        const messages = [
            {
                role: "user",
                content: [
                    { type: "text", text: prompt },
                    { type: "image_url", image_url: { url: base64Image } }
                ]
            }
        ];
        
        // Use gpt-4o-mini which supports vision and is faster
        // Pass the messages array as the first argument.
        const chatResponse = await window.puter.ai.chat(messages, { model: 'gpt-4o-mini' });
        
        // Parse Response
        let description = '';
        if (typeof chatResponse === 'string') {
            description = chatResponse;
        } else if (chatResponse?.message?.content) {
             description = chatResponse.message.content;
        } else if (chatResponse?.result?.message?.content) {
             description = chatResponse.result.message.content;
        }

        if (description) {
            target.dataset.aiVisionDescription = description;
            this.showCaption(description);
        } else {
            this.showCaption("Nie udało się opisać obrazka.");
        }

    } catch (e) {
        console.error("AI Vision Error:", e);
        this.showCaption("Błąd analizy obrazu.");
    } finally {
         document.body.style.cursor = originalCursor;
    }
  }

  showCaption(text) {
      let captionBox = document.getElementById('a11y-image-caption');
      if (!captionBox) {
          captionBox = document.createElement('div');
          captionBox.id = 'a11y-image-caption';
          captionBox.className = 'a11y-image-caption';
          document.body.appendChild(captionBox);
          
          // Add close button functionality
          captionBox.addEventListener('click', () => {
              captionBox.style.display = 'none';
          });
      }
      
      captionBox.textContent = text;
      captionBox.style.display = 'block';
      
      // Auto-hide after 10 seconds? Maybe not, let user close it.
      // But clearing previous timeouts if any.
  }

  stopPlayback() {
     // No audio to stop anymore
  }
}
